import { useState } from 'react';
import { CompassCardComponentProps } from '@scale/compass-ui/types';
import { Tabs, TextArea, TextField, FormField, Box, Flex } from '@scale/scaleui-radix';
import useTaskAdapterCard from './useTaskAdapterCard';
import { TASK_ADAPTER_INPUT_COLUMNS } from './constants';
import { Filters } from '@scale/compass-ui/components/filters/Filters';
import { COLUMN_ID_TO_OPTIONS } from 'react-scale/corp/views/DataCompassCardsV2/components/filters/inputFilterOptions';
import { parseAnythingSeparatedIds } from '@scale/shared/utils';
import classNames from 'classnames';
import type { TaskAdapterContext } from '@scale/genai-compass-shared/cards/tasks';

type TaskAdapterCardTab = 'Search' | 'Filter';

export default function TaskAdapterCardComponent({
  context,
  updateCardContext,
  updateCardCanSubmit,
}: CompassCardComponentProps<TaskAdapterContext>) {
  const {
    filter,
    setFilter,
    searchMode,
    setSearchMode,
    setTaskIds,
    taskIds,
    limit,
    setLimit,
    projectId,
    setProjectId,
    allowEditProjectId,
  } = useTaskAdapterCard(context, updateCardContext, updateCardCanSubmit);
  const [searchInput, setSearchInput] = useState<string>(taskIds?.join(',') ?? '');

  const [currentTab, setCurrentTab] = useState<TaskAdapterCardTab>(
    searchMode === 'Search' ? 'Search' : 'Filter',
  );

  const onTabChange = (val: string) => {
    const tab = val as TaskAdapterCardTab;
    setCurrentTab(tab);
    setSearchMode(tab);
  };

  const onSearchInputChange = (input: string) => {
    setSearchInput(input);
    setTaskIds(parseAnythingSeparatedIds(input));
  };

  return (
    <div className="py-2">
      <div className="space-y-4 mb-3">
        <Tabs.Root onValueChange={onTabChange} value={currentTab} defaultValue="Search">
          <Tabs.List>
            <Tabs.Trigger value="Search">Task IDs</Tabs.Trigger>
            <Tabs.Trigger value="Filter">Filter</Tabs.Trigger>
          </Tabs.List>
          <Box pt="4">
            <div hidden={!allowEditProjectId}>
              <FormField.Root
                size="3"
                className={classNames('mb-3', currentTab === 'Filter' ? 'flex-row gap-4' : '')}
              >
                <FormField.Label
                  htmlFor="project-id-input"
                  className={classNames(
                    'w-20',
                    currentTab === 'Filter' ? 'text-right justify-end' : '',
                  )}
                >
                  Project ID
                </FormField.Label>
                <TextField.Root
                  id="project-id-input"
                  className="flex-grow"
                  value={projectId}
                  onChange={e => setProjectId(e.target.value)}
                />
              </FormField.Root>
            </div>
            <Tabs.Content value="Search">
              {currentTab === 'Search' && (
                <FormField.Root size="3">
                  <FormField.Label htmlFor="task-search-textarea">
                    Enter Task IDs to fetch
                  </FormField.Label>
                  <TextArea
                    id="search-textarea"
                    placeholder="Comma (,), space, or newline separated"
                    value={searchInput}
                    onChange={e => onSearchInputChange(e.target.value)}
                  />
                </FormField.Root>
              )}
            </Tabs.Content>
            <Tabs.Content value="Filter">
              {currentTab === 'Filter' && (
                <Flex direction="column" gap="2" align="end">
                  <Filters
                    columns={Object.values(TASK_ADAPTER_INPUT_COLUMNS)}
                    filters={filter ?? { operator: 'and', filtersOrFilterGroups: [] }}
                    setFilters={setFilter}
                    columnOptionMap={COLUMN_ID_TO_OPTIONS}
                    disabledOperators={['or']}
                    disableNested={true}
                    disabledConditions={{
                      text: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
                      number: ['is_empty', 'is_not_empty'],
                    }}
                    allowZeroFilters={true}
                  />
                  <FormField.Label>Preview # of tasks</FormField.Label>
                  <Box maxWidth="70px">
                    <TextField.Root
                      type="number"
                      value={limit}
                      onChange={e => setLimit(e.target.value)}
                      placeholder="limit"
                      min={1}
                    />
                  </Box>
                </Flex>
              )}
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </div>
    </div>
  );
}
